import type { Office } from "@homeinstead/schemas";
import type { RoutePageContext } from "@homeinstead/site-builder";

/** Fallback phone number used when no office or metadata phone is available. */
const FALLBACK_PHONE_NUMBER = "(888) 673-1166";

/**
 * Gets the phone number from route page context with fallback
 *
 * Resolution order (highest to lowest priority):
 * 1. office.phone
 * 2. office.phoneAlt
 * 3. context.metadata.phone_number (dictionaryItem on the route node)
 * 4. fallback
 *
 * The metadata phone_number allows PPC/national pages without an office context
 * to configure a nav bar phone number directly on the route node in Studio.
 *
 * @param context - The route page context that may contain office and other information
 * @param fallback - The fallback phone number to use if no other phone is found
 * @returns The phone number (trimmed) or the fallback number
 *
 * @todo - move to site-builder package
 */
function getPhoneFromContext(
  context: RoutePageContext | null | undefined,
  fallback: string,
): string {
  const office = context?.office as Office;

  const officePhone = office?.phone?.trim() || office?.phoneAlt?.trim();

  if (officePhone) {
    return officePhone;
  }

  const metadataPhone = (
    context?.metadata as Record<string, string> | undefined
  )?.phone_number?.trim();

  if (metadataPhone) {
    return metadataPhone;
  }

  return fallback;
}

export { FALLBACK_PHONE_NUMBER, getPhoneFromContext };
